import React, { useState, useRef, useCallback, useEffect } from 'react';
import { bilderFranUrklipp, MAX_BILDER } from '../../../utils/clipboardImages';
import { laddaUppGranskningsbilder } from '../utils/granskningsbilder';
import {
  PdfHighlighter,
  PdfLoader,
  TextHighlight,
  AreaHighlight,
  MonitoredHighlightContainer,
  useHighlightContainerContext,
  usePdfHighlighterContext
} from 'react-pdf-highlighter-plus';
import 'react-pdf-highlighter-plus/style/style.css';

// Matcha PDF.js worker-version med den installerade pdfjs-dist-versionen
const PDFJS_VERSION = '4.10.38';
const WORKER_SRC = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

const getNextId = () => String(Math.random()).slice(2);

const PANEL_NYCKEL = 'ritningsgranskning.kommentarspanel';

const ROLE_LABELS = {
  kund: 'Kund',
  inredningsarkitekt: 'IA',
  byggkonsult: 'BK',
  admin: 'Admin',
  byggare: 'Byggare',
  UE: 'UE',
  projektor: 'Projektör'
};

const ROLE_COLORS = {
  kund: 'bg-blue-100 text-blue-700',
  inredningsarkitekt: 'bg-purple-100 text-purple-700',
  byggkonsult: 'bg-green-100 text-green-700',
  admin: 'bg-gray-100 text-gray-700'
};

// Highlight-färger per roll (bakgrund + cirkel)
const HIGHLIGHT_COLORS = {
  kund: { bg: 'rgba(251, 191, 36, 0.3)', circle: '#F59E0B' },           // Gul
  inredningsarkitekt: { bg: 'rgba(252, 90, 141, 0.3)', circle: '#F07D91' } // Rosa
};

// Delad ref för att ge HighlightRenderer tillgång till highlights-listan
// (PdfHighlighter skickar inte vidare custom props till child-komponenter)
let sharedHighlightsRef = { current: [] };

const HighlightRenderer = ({ onUpdate, readOnly, currentRole }) => {
  const { highlight, viewportToScaled, screenshot, isScrolledTo } = useHighlightContainerContext();
  const { setTip } = usePdfHighlighterContext();
  const wrapperRef = useRef(null);

  const highlightIndex = sharedHighlightsRef.current.findIndex(h => h.id === highlight.id);
  // Biblioteket strippar custom props — hämta authorRole från vår egna ref
  const authorRole = sharedHighlightsRef.current[highlightIndex]?.authorRole || highlight.authorRole;

  const isAreaHighlight = highlight.type === 'area' || highlight.content?.image;

  // Direkt DOM-manipulation — sätter bakgrund + CSS custom properties för ::before.
  // MutationObserver behövs eftersom biblioteket re-renderar highlight-element
  // vid interaktioner (klick, fokus), vilket nollställer våra inline styles.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const colors = HIGHLIGHT_COLORS[authorRole] || HIGHLIGHT_COLORS.kund;
    let applying = false;
    const applyStyles = () => {
      if (applying) return;
      applying = true;
      const parts = el.querySelectorAll('.TextHighlight__part, .AreaHighlight__part');
      parts.forEach(part => {
        part.style.setProperty('background', colors.bg, 'important');
        part.style.setProperty('--hl-circle', colors.circle);
        part.style.setProperty('--hl-index', `'${highlightIndex >= 0 ? highlightIndex + 1 : ''}'`);
      });
      applying = false;
    };
    applyStyles();
    const observer = new MutationObserver(applyStyles);
    observer.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    return () => observer.disconnect();
  }, [authorRole, highlightIndex, highlight.id]);

  // Visa hela kommentarstråden som tooltip
  const allComments = highlight.comments?.length > 0
    ? highlight.comments
    : highlight.comment?.text ? [{ text: highlight.comment.text }] : [];

  const tipContent = allComments.length > 0 ? (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 max-w-xs space-y-1.5">
      {allComments.map((c, i) => (
        <div key={i} className={i > 0 ? 'border-t border-white/20 pt-1.5' : ''}>
          {c.authorName && (
            <span className="font-semibold text-white/90">{c.authorName}: </span>
          )}
          <span>{c.text}</span>
        </div>
      ))}
    </div>
  ) : null;

  // Bara den som skapat markeringen får flytta eller ändra storlek på den.
  // Utan det kan en kund dra omkring IA:s markeringar, och tvärtom — och den
  // som ritade dem ser plötsligt att de pekar på fel ställe.
  const arEgenMarkering = !authorRole || !currentRole || authorRole === currentRole;
  const farFlyttas = !readOnly && arEgenMarkering;

  const component = isAreaHighlight ? (
    <AreaHighlight
      highlight={highlight}
      isScrolledTo={isScrolledTo}
      onChange={(boundingRect) => {
        if (farFlyttas) {
          onUpdate(highlight.id, {
            boundingRect: viewportToScaled(boundingRect)
          }, {
            image: screenshot(boundingRect)
          });
        }
      }}
    />
  ) : (
    <TextHighlight
      highlight={highlight}
      isScrolledTo={isScrolledTo}
    />
  );

  return (
    <MonitoredHighlightContainer
      highlightTip={tipContent ? { position: highlight.position, content: tipContent } : undefined}
      onMouseEnter={() => {
        if (tipContent) {
          setTip({ position: highlight.position, content: tipContent });
        }
      }}
    >
      {/*
        AreaHighlight är alltid dragbar via react-rnd och har ingen inställning
        för att låsa den. Att bara utelämna onChange hindrar att flytten sparas,
        men användaren kan ändå dra runt rutan på skärmen. Genom att fånga
        mousedown/touchstart innan react-rnd ser dem startar draget aldrig, medan
        hover fortfarande fungerar så kommentaren går att läsa.
      */}
      <div
        ref={wrapperRef}
        data-hl-id={highlight.id}
        style={{ display: 'contents' }}
        onMouseDownCapture={farFlyttas ? undefined : (e) => { e.preventDefault(); e.stopPropagation(); }}
        onTouchStartCapture={farFlyttas ? undefined : (e) => { e.stopPropagation(); }}
      >
        {component}
      </div>
    </MonitoredHighlightContainer>
  );
};

const PdfAnnotationViewer = ({
  pdfUrl,
  initialAnnotations = [],
  readOnly = false,
  canReply = false,
  currentUser = null,
  authorRole = null,
  onAnnotationsChange,
  onReply,
  // Redigera/radera ETT inlägg i en tråd. Sparas direkt mot servern, till
  // skillnad från onAnnotationsChange som bara rör lokalt state.
  onEditComment,
  onDeleteComment,
  // Anropas när ett ÄNNU INTE SKICKAT inlägg tas bort, så att föräldern kan
  // släppa sitt eget väntande tillstånd. Utan den låg svaret kvar i
  // pendingReplies och skickades ändå vid nästa "Skicka".
  onRemovePendingComment,
  // Samma sak när ett osänt inlägg ÄNDRAS: texten i tråden räcker inte, det är
  // förälderns väntande tillstånd som skickas. Utan den skickades den gamla
  // texten trots att tråden visade den nya.
  onEditPendingComment,
  // Krävs för att kunna ladda upp inklistrade skärmbilder. Utan den är
  // inklistring avstängd (se hanteraBildPaste).
  projectId = null
}) => {
  const [highlights, setHighlights] = useState(initialAnnotations);
  const [editingId, setEditingId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  // Inklistrade skärmbilder till svaret som skrivs just nu. S3-URL:er, inte
  // File-objekt — uppladdningen sker vid inklistring, se laddaUppGranskningsbilder.
  const [replyBilder, setReplyBilder] = useState([]);
  const [laddarBild, setLaddarBild] = useState(false);
  const [bildFel, setBildFel] = useState(null);
  // Vilket enskilt inlägg som redigeras: "<annoterings-id>|<inläggs-id>"
  const [redigerarInlagg, setRedigerarInlagg] = useState(null);
  const [redigerarText, setRedigerarText] = useState('');
  // 'page-width' fyller den tillgängliga bredden, till skillnad från 'auto' som
  // kan lämna ritningen liten på breda skärmar och surfplattor.
  const [scale, setScale] = useState('page-width');
  /**
   * Områdesläge: dra för att markera utan att hålla Alt/Option.
   *
   * Alt + dra var enda vägen till en områdesmarkering, och en kund på Mac fick
   * det inte att fungera — tangenten heter Option där, och på en styrplatta
   * med tryck-för-att-klicka blir "klicka och dra" lätt bara ett klick.
   * Ritningar saknar dessutom ofta textlager, så områdesmarkering är ofta det
   * enda sättet att kommentera. Alt + dra fungerar som förut.
   */
  const [omradesLage, setOmradesLage] = useState(false);
  const utilsRef = useRef(null);
  // Kommentarspanelen går att fälla ihop så att ritningen får hela ytan på små
  // skärmar. Valet minns per webbläsare. Biblioteket följer behållarens storlek
  // med en ResizeObserver, så 'page-width' fyller ut den frigjorda ytan själv.
  const [panelOppen, setPanelOppen] = useState(() => {
    try { return localStorage.getItem(PANEL_NYCKEL) !== 'stangd'; } catch { return true; }
  });
  const vaxlaPanel = useCallback(() => {
    setPanelOppen(prev => {
      try { localStorage.setItem(PANEL_NYCKEL, prev ? 'stangd' : 'oppen'); } catch { /* privat läge */ }
      return !prev;
    });
  }, []);

  useEffect(() => {
    setHighlights(initialAnnotations);
  }, [initialAnnotations]);

  // Håll sharedHighlightsRef synkad — synkront i varje setState-callback
  // (useEffect kör efter render, men HighlightRenderer läser ref under render)
  // Föräldern får INTE uppdateras inifrån setHighlights — React kör den
  // funktionen under rendering, vilket ger "Cannot update a component while
  // rendering a different component" och i värsta fall en renderingsloop som
  // låser editorn. Lägg undan värdet och notifiera efter att renderingen
  // committats i stället.
  const attNotifieraRef = useRef(null);

  const syncAndSet = useCallback((updater) => {
    setHighlights(prev => {
      const updated = typeof updater === 'function' ? updater(prev) : updater;
      sharedHighlightsRef.current = updated;
      attNotifieraRef.current = updated;
      return updated;
    });
  }, []);

  useEffect(() => {
    if (attNotifieraRef.current) {
      const updated = attNotifieraRef.current;
      attNotifieraRef.current = null;
      onAnnotationsChange?.(updated);
    }
  });

  // Synka vid initialAnnotations-ändring
  useEffect(() => {
    sharedHighlightsRef.current = highlights;
  }, [highlights]);

  const addHighlight = useCallback((highlight) => {
    const newHighlight = { ...highlight, id: getNextId(), authorRole: authorRole || undefined };
    syncAndSet(prev => [...prev, newHighlight]);
    setEditingId(newHighlight.id);
    // En ny markering behöver sin kommentarsruta — öppna panelen om den är
    // ihopfälld, annars syns inte var man ska skriva.
    setPanelOppen(true);
  }, [syncAndSet, authorRole]);

  const updateHighlight = useCallback((highlightId, position, content) => {
    syncAndSet(prev => prev.map(h => {
      if (h.id === highlightId) {
        return {
          ...h,
          position: { ...h.position, ...position },
          content: { ...h.content, ...content }
        };
      }
      return h;
    }));
  }, [syncAndSet]);

  const updateHighlightComment = useCallback((highlightId, commentText) => {
    syncAndSet(prev => prev.map(h => {
      if (h.id === highlightId) {
        return { ...h, comment: { ...h.comment, text: commentText } };
      }
      return h;
    }));
  }, [syncAndSet]);

  const updateHighlightCommentImages = useCallback((highlightId, nyaUrler) => {
    syncAndSet(prev => prev.map(h => {
      if (h.id !== highlightId) return h;
      const befintliga = h.comment?.images || [];
      return {
        ...h,
        comment: { ...h.comment, images: [...befintliga, ...nyaUrler].slice(0, MAX_BILDER) }
      };
    }));
  }, [syncAndSet]);

  const taBortHighlightBild = useCallback((highlightId, url) => {
    syncAndSet(prev => prev.map(h => {
      if (h.id !== highlightId) return h;
      return { ...h, comment: { ...h.comment, images: (h.comment?.images || []).filter(u => u !== url) } };
    }));
  }, [syncAndSet]);

  /**
   * Gemensam inklistring för markeringarnas kommentarsrutor.
   *
   * Laddar upp direkt och lämnar URL:erna till `mottag`. Ingen hook per ruta:
   * varje markering har sin egen kommentarsruta, och ett delat hook-tillstånd
   * hade blandat bilder mellan markeringar. Highlight-listan ÄR tillståndet.
   */
  const hanteraBildPaste = useCallback(async (e, antalBefintliga, mottag) => {
    const nya = bilderFranUrklipp(e);
    // Inga bilder i urklippet: rör inte händelsen, annars går vanlig
    // textinklistring förlorad.
    if (nya.length === 0) return;
    e.preventDefault();

    if (!projectId) {
      setBildFel('Bilder kan inte laddas upp här.');
      return;
    }
    const plats = MAX_BILDER - antalBefintliga;
    if (plats <= 0) {
      setBildFel(`Max ${MAX_BILDER} bilder per kommentar.`);
      return;
    }

    setLaddarBild(true);
    setBildFel(null);
    try {
      mottag(await laddaUppGranskningsbilder(projectId, nya.slice(0, plats)));
    } catch (err) {
      setBildFel(err.message || 'Kunde inte ladda upp bilden');
    } finally {
      setLaddarBild(false);
    }
  }, [projectId]);

  const removeHighlight = useCallback((highlightId) => {
    syncAndSet(prev => prev.filter(h => h.id !== highlightId));
    if (editingId === highlightId) setEditingId(null);
  }, [syncAndSet, editingId]);

  const handleReply = useCallback(async (annotationId) => {
    if (!onReply) return;
    // Ett svar får vara bara en bild — en skärmbild kan vara hela svaret.
    if (!replyText.trim() && replyBilder.length === 0) return;
    await onReply(annotationId, replyText.trim(), replyBilder);
    setReplyText('');
    setReplyBilder([]);
    setBildFel(null);
    setReplyingTo(null);
  }, [replyText, replyBilder, onReply]);

  // Inlägget är mitt om författar-id matchar. Äldre inlägg saknar authorId och
  // faller tillbaka på rollen — då kan man redigera sitt eget även i gamla trådar.
  const arEgetInlagg = useCallback((c) => {
    if (!c) return false;
    if (c.authorId && currentUser?._id) return String(c.authorId) === String(currentUser._id);
    return !!authorRole && c.authorRole === authorRole;
  }, [currentUser, authorRole]);

  // Inlägg skapade innan kommentarer fick id identifieras på plats i listan.
  // Backend har samma index:N-fallback i editComment och deleteComment.
  const inlaggsId = (c, index) => c?.id || `index:${index}`;

  /**
   * Spara ändringen i ett eget inlägg.
   *
   * Ett inlägg som ännu inte skickats finns inte på servern, så ändringen måste
   * ske lokalt — precis som raderingen nedan. Tidigare gick ALLA ändringar till
   * servern: ett osänt svar saknar id, föll tillbaka på index:N, och eftersom
   * serverns tråd är kortare än den lokala svarade den "Kommentaren hittades
   * inte". IA kunde alltså inte rätta en formulering förrän svaret var skickat.
   *
   * Värre i det smala fallet: är det lokala utkastet äldre än serverns tråd kan
   * index:N peka på ett BEFINTLIGT inlägg. Är det ens eget skrevs fel inläggs
   * text över, tyst.
   *
   * Flaggan pending avgör, inte frånvaron av id: inlägg som skapades innan
   * kommentarer fick id saknar också id, och för dem ÄR index:N ett giltigt
   * server-id (se inlaggsId och backendens editComment).
   */
  const sparaInlaggsredigering = useCallback(async (annotationId, commentId, kommentar) => {
    const nyText = redigerarText.trim();
    const arOsant = kommentar?.pending === true;
    if (!nyText || (!arOsant && !onEditComment)) return;

    if (arOsant) {
      syncAndSet(prev => prev.map(h => {
        if (h.id !== annotationId) return h;
        return {
          ...h,
          comments: (h.comments || []).map(c => (c === kommentar ? { ...c, text: nyText } : c))
        };
      }));
      onEditPendingComment?.(annotationId, kommentar, nyText);
    } else {
      await onEditComment(annotationId, commentId, nyText);
    }

    setRedigerarInlagg(null);
    setRedigerarText('');
  }, [redigerarText, onEditComment, onEditPendingComment, syncAndSet]);

  /**
   * Ta bort ett eget inlägg i en tråd.
   *
   * Ett inlägg som ännu inte skickats finns inte på servern, så raderingen måste
   * ske lokalt. Tidigare gick ALLA raderingar till servern, och ett osänt svar
   * gav därför "Kommentaren hittades inte" — medan bilden låg kvar.
   *
   * Flaggan pending avgör, inte frånvaron av id: inlägg som skapades innan
   * kommentarer fick id saknar också id, och för dem är index:N ett giltigt
   * server-id (se inlaggsId och backendens editComment/deleteComment).
   */
  const raderaInlagg = useCallback(async (annotationId, commentId, kommentar) => {
    const arOsant = kommentar?.pending === true;
    if (!arOsant && !onDeleteComment) return;

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Ta bort ditt inlägg? Resten av tråden ligger kvar.')) return;

    if (arOsant) {
      syncAndSet(prev => prev.map(h => {
        if (h.id !== annotationId) return h;
        return { ...h, comments: (h.comments || []).filter(c => c !== kommentar) };
      }));
      onRemovePendingComment?.(annotationId, kommentar);
      return;
    }

    await onDeleteComment(annotationId, commentId);
  }, [onDeleteComment, onRemovePendingComment, syncAndSet]);

  /**
   * Ta bort en markering som ännu inte har någon tråd.
   *
   * Knappen tog tidigare markeringen med ALLA inlägg, utan att fråga, och satt
   * direkt under svarsrutan — den lästes som "ta bort mitt svar". En
   * inredningsarkitekt raderade på det viset kundens frågor tillsammans med sitt
   * eget svar. Nu visas den bara innan någon svarat; i en tråd tar man bort sitt
   * eget inlägg rad för rad.
   */
  const raderaMarkering = useCallback((h) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Ta bort markeringen?')) return;
    removeHighlight(h.id);
  }, [removeHighlight]);

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Ingen PDF vald
      </div>
    );
  }

  return (
    // min-h-0 krävs för att flex-barnen ska få krympa — utan den blir PDF-ytan
    // högre än sin container, klipps av overflow-hidden och går inte att skrolla
    // vertikalt. (Tidigare låg här minHeight: 600px, vilket orsakade exakt det på
    // låga skärmar.) Under lg staplas PDF och kommentarer i stället för att trängas.
    <div className="h-full min-h-0 flex flex-col lg:flex-row">
      {/* Fixa textlagret så det ligger osynligt ovanpå canvasen */}
      <style>{`
        /* Dölj PDF:ens inbyggda annotations (kommentarer, popups, sticky notes etc.)
           så att enbart våra egna kommentarer visas */
        .annotationLayer,
        .annotationLayer *,
        .annotationLayer section,
        .annotationLayer .popupAnnotation,
        .annotationLayer .popupWrapper,
        .annotationLayer .popup {
          display: none !important;
          visibility: hidden !important;
        }

        /* pdf.js renderar canvasen i skärmens pixeltäthet, men dess CSS-storlek
           blir kvar på renderingsupplösningen. Markeringslagret positionerar mot
           sidrutan, så ritningen och markeringarna hamnar i olika skalor så fort
           pixeltätheten inte är 1 — en markering syns bara rätt på en skärm med
           samma pixeltäthet som den skapades på. Bind bilden till sidrutan.
           Den interna upplösningen är orörd, så skärpan påverkas inte. */
        .page canvas {
          width: 100% !important;
          height: 100% !important;
        }

        .textLayer {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          overflow: hidden !important;
        }
        .textLayer span,
        .textLayer br {
          color: transparent !important;
          position: absolute !important;
        }
        .textLayer .highlight {
          background-color: rgba(252, 90, 141, 0.3);
        }
        .textLayer ::selection {
          background: rgba(252, 90, 141, 0.3);
        }
        .TextHighlight .TextHighlight__part:first-child::before,
        .AreaHighlight .AreaHighlight__part::before {
          content: var(--hl-index, '');
          position: absolute;
          top: -10px;
          left: -10px;
          min-width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: var(--hl-circle, #F59E0B);
          color: white;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 30;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          pointer-events: none;
          line-height: 1;
          padding: 0 4px;
        }
      `}</style>

      {/* PDF-vy */}
      <div className={`flex-1 min-h-0 relative overflow-hidden ${omradesLage ? '[&_.page]:cursor-crosshair' : ''}`}>
        {/* Områdesläge — samma platta som zoomen, i motsatta hörnet */}
        {!readOnly && (
          <div className="absolute top-3 left-3 z-20">
            <button
              type="button"
              onClick={() => setOmradesLage(v => !v)}
              aria-pressed={omradesLage}
              title={omradesLage
                ? 'Stäng av områdesmarkering'
                : 'Dra med musen för att markera ett område på ritningen'}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border shadow-sm text-xs font-medium transition-colors ${
                omradesLage
                  ? 'bg-leadme-pink border-leadme-pink text-white hover:bg-leadme-pink-dark'
                  : 'bg-white/90 backdrop-blur-sm border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <rect x="4" y="4" width="16" height="16" rx="1" strokeWidth={2} strokeDasharray="3 2" />
              </svg>
              {omradesLage ? 'Markera område: på' : 'Markera område'}
            </button>
          </div>
        )}

        {/* Zoom-kontroller */}
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm px-1 py-0.5">
          <button
            onClick={() => setScale(prev => {
              const current = typeof prev === 'number' ? prev : 1;
              return Math.max(0.25, current - 0.25);
            })}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-600 text-lg font-bold"
            title="Zooma ut"
          >
            −
          </button>
          <button
            onClick={() => setScale('page-width')}
            className="px-1.5 h-7 flex items-center justify-center rounded hover:bg-gray-100 text-xs text-gray-600 font-medium min-w-[3rem]"
            title="Anpassa till fönster"
          >
            {typeof scale === 'number' ? `${Math.round(scale * 100)}%` : 'Anpassa'}
          </button>
          <button
            onClick={() => setScale(prev => {
              const current = typeof prev === 'number' ? prev : 1;
              return Math.min(5, current + 0.25);
            })}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-600 text-lg font-bold"
            title="Zooma in"
          >
            +
          </button>
        </div>

        {/*
          key på pdfUrl tvingar fram en helt ny laddare när dokumentet byts.
          Utan den återanvänds den gamla instansen, och bibliotekets fördröjda
          resize-hanterare hinner köra setDocument mot en redan riven transport
          — vilket kraschar med "Cannot read properties of null (sendWithPromise)".
        */}
        <PdfLoader
          key={pdfUrl}
          document={pdfUrl}
          workerSrc={WORKER_SRC}
          beforeLoad={() => (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-leadme-pink"></div>
            </div>
          )}
        >
          {(pdfDocument) => (
            <PdfHighlighter
              pdfDocument={pdfDocument}
              pdfScaleValue={scale}
              highlights={highlights}
              enableAreaSelection={(event) => !readOnly && (omradesLage || event.altKey)}
              utilsRef={(utils) => { utilsRef.current = utils; }}
              onSelection={(selection) => {
                if (readOnly) return;
                const ghost = selection.makeGhostHighlight();
                addHighlight({
                  content: ghost.content,
                  position: ghost.position,
                  type: ghost.type,
                  comment: { text: '' }
                });
              }}
              selectionTip={!readOnly ? (
                <div className="bg-leadme-pink text-white text-xs rounded px-2 py-1 cursor-pointer">
                  Markera
                </div>
              ) : undefined}
            >
              <HighlightRenderer onUpdate={updateHighlight} readOnly={readOnly} currentRole={authorRole} />
            </PdfHighlighter>
          )}
        </PdfLoader>
      </div>

      {/* Ihopfälld: en smal list bredvid ritningen från lg, en rad under den
          på smalare skärmar. Hela listen är knappen. */}
      {!panelOppen && (
        <button
          type="button"
          onClick={vaxlaPanel}
          title="Visa kommentarerna"
          className="w-full lg:w-10 flex-shrink-0 bg-gray-50 border-t lg:border-t-0 lg:border-l flex lg:flex-col items-center justify-center lg:justify-start gap-2 px-3 py-2 lg:px-0 lg:py-3 text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <svg className="w-4 h-4 flex-shrink-0 rotate-90 lg:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium lg:[writing-mode:vertical-rl]">
            Kommentarer {highlights.length > 0 && `(${highlights.length})`}
          </span>
        </button>
      )}

      {/* Sidebar med kommentarer — under PDF:en på smala skärmar, bredvid från lg.
          max-h under lg gör att den aldrig äter upp hela PDF-ytan. */}
      {panelOppen && (
      <div className="w-full lg:w-80 flex-shrink-0 max-h-[40%] lg:max-h-none bg-white border-t lg:border-t-0 lg:border-l overflow-y-auto flex flex-col">
        <div className="p-3 border-b bg-gray-50 flex items-center justify-between gap-2">
          <h4 className="text-sm font-medium text-gray-900">
            Kommentarer {highlights.length > 0 && `(${highlights.length})`}
          </h4>
          <button
            type="button"
            onClick={vaxlaPanel}
            title="Fäll ihop kommentarerna och visa ritningen större"
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            Dölj
            <svg className="w-4 h-4 -rotate-90 lg:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        {highlights.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <svg className="mx-auto h-8 w-8 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              {!readOnly ? (
                <>
                  <p className="text-xs text-gray-500">Markera text i PDF:en för att lägga till en kommentar.</p>
                  <p className="text-xs text-gray-400 mt-1">Håll <kbd className="bg-gray-100 border border-gray-300 rounded px-1 py-0.5 text-[10px] font-mono">Alt</kbd> + dra för områdesmarkering.</p>
                </>
              ) : (
                <p className="text-xs text-gray-500">Inga kommentarer.</p>
              )}
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 flex-1">
            {highlights.map((h, index) => {
              const isEditing = editingId === h.id;
              const isReplying = replyingTo === h.id;
              const label = h.content?.text
                ? h.content.text.slice(0, 60) + (h.content.text.length > 60 ? '...' : '')
                : 'Områdesmarkering';

              // Kommentarstråd: comments-array eller fallback till comment.text
              const comments = h.comments && h.comments.length > 0
                ? h.comments
                : (h.comment?.text ? [{ text: h.comment.text }] : []);

              return (
                <li
                  key={h.id}
                  className={`p-3 text-sm cursor-pointer transition-colors ${isEditing ? 'bg-pink-50' : 'hover:bg-gray-50'}`}
                  onClick={() => {
                    utilsRef.current?.scrollToHighlight(h);
                    const isOwn = !h.authorRole || h.authorRole === authorRole;
                    if (!readOnly && isOwn) setEditingId(h.id);
                  }}
                >
                  {/* Markeringsnummer + markerad text */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold mr-1.5"
                        style={{ backgroundColor: HIGHLIGHT_COLORS[h.authorRole]?.circle || HIGHLIGHT_COLORS.kund.circle }}
                      >
                        {index + 1}
                      </span>
                      <span className="text-xs text-gray-500">{label}</span>
                    </div>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      s. {h.position?.boundingRect?.pageNumber}
                    </span>
                  </div>

                  {/* Kommentarer — redigeringsläge (kund) */}
                  {!readOnly && !h.comments?.length ? (
                    <div className="mt-2">
                      {isEditing ? (
                        <textarea
                          autoFocus
                          value={h.comment?.text || ''}
                          onChange={(e) => updateHighlightComment(h.id, e.target.value)}
                          onBlur={() => setEditingId(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              setEditingId(null);
                            }
                          }}
                          onPaste={(e) => hanteraBildPaste(
                            e,
                            (h.comment?.images || []).length,
                            (urler) => updateHighlightCommentImages(h.id, urler)
                          )}
                          placeholder="Skriv din kommentar här, eller klistra in en skärmbild..."
                          rows={2}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink resize-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <p
                          className={`text-xs mt-1 ${h.comment?.text ? 'text-gray-700' : 'text-gray-400 italic'}`}
                          onClick={(e) => { e.stopPropagation(); setEditingId(h.id); }}
                        >
                          {h.comment?.text || 'Klicka för att kommentera...'}
                        </p>
                      )}
                      {(h.comment?.images || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {h.comment?.images.map((url, bi) => (
                            <div key={bi} className="relative">
                              <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                <img src={url} alt="" className="h-12 w-12 object-cover rounded border border-gray-300 hover:opacity-80 transition-opacity" />
                              </a>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); taBortHighlightBild(h.id, url); }}
                                className="absolute -top-1 -right-1 bg-white border border-gray-300 rounded-full w-4 h-4 text-[9px] leading-none text-gray-500 hover:text-red-600"
                                title="Ta bort bilden"
                              >&times;</button>
                            </div>
                          ))}
                        </div>
                      )}
                      {laddarBild && <p className="text-[10px] text-gray-500 mt-1">Laddar upp bilden…</p>}
                      {bildFel && <p className="text-[10px] text-red-600 mt-1">{bildFel}</p>}
                    </div>
                  ) : (
                    /* Kommentarstråd — visningsläge (submitted review) */
                    <div className="mt-2 space-y-2">
                      {comments.map((c, ci) => {
                        const cId = inlaggsId(c, ci);
                        const redigeras = redigerarInlagg === `${h.id}|${cId}`;
                        // Knapparna kräver att föräldern kan spara — annars är
                        // vyn read-only och ska inte låtsas annat.
                        const egetOchRedigerbart = arEgetInlagg(c) && !readOnly && (onEditComment || onDeleteComment);
                        return (
                          <div key={ci} className="text-xs group">
                            {c.authorName ? (
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="font-semibold text-gray-800">{c.authorName}</span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${ROLE_COLORS[c.authorRole] || 'bg-gray-100 text-gray-600'}`}>
                                  {ROLE_LABELS[c.authorRole] || c.authorRole}
                                </span>
                                {c.createdAt && (
                                  <span className="text-[10px] text-gray-400">
                                    {new Date(c.createdAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                                  </span>
                                )}
                                {c.editedAt && (
                                  <span className="text-[10px] text-gray-400 italic">redigerad</span>
                                )}
                                {/* Utkast: skrivet men inte skickat. Byggkonsulten
                                    ser kundens utkast — de säger vad kunden
                                    funderar på — men ska inte läsa dem som ett
                                    besked. Egna utkast märks lika, så man ser
                                    att de ännu inte gått iväg. */}
                                {c.draft && (
                                  <span
                                    title={arEgetInlagg(c)
                                      ? 'Du har inte skickat den här ännu'
                                      : `${c.authorName || 'Avsändaren'} har skrivit men inte skickat den här`}
                                    className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800"
                                  >
                                    Utkast
                                  </span>
                                )}
                                {/* Egna inlägg: synliga knappar. Tidigare fick man
                                    gissa att texten var klickbar. */}
                                {egetOchRedigerbart && !redigeras && (
                                  <span className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    {onEditComment && (
                                      <button
                                        onClick={() => { setRedigerarInlagg(`${h.id}|${cId}`); setRedigerarText(c.text || ''); }}
                                        title="Redigera ditt inlägg"
                                        className="text-gray-400 hover:text-leadme-pink p-0.5"
                                      >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                    )}
                                    {onDeleteComment && (
                                      <button
                                        onClick={() => raderaInlagg(h.id, cId, c)}
                                        title="Ta bort ditt inlägg"
                                        className="text-gray-400 hover:text-red-600 p-0.5"
                                      >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    )}
                                  </span>
                                )}
                              </div>
                            ) : null}
                            {redigeras ? (
                              <div onClick={(e) => e.stopPropagation()}>
                                <textarea
                                  autoFocus
                                  value={redigerarText}
                                  onChange={(e) => setRedigerarText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      sparaInlaggsredigering(h.id, cId, c);
                                    }
                                    if (e.key === 'Escape') { setRedigerarInlagg(null); setRedigerarText(''); }
                                  }}
                                  rows={2}
                                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink resize-none"
                                />
                                <div className="flex justify-end gap-1 mt-1">
                                  <button
                                    onClick={() => { setRedigerarInlagg(null); setRedigerarText(''); }}
                                    className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-1"
                                  >
                                    Avbryt
                                  </button>
                                  <button
                                    onClick={() => sparaInlaggsredigering(h.id, cId, c)}
                                    disabled={!redigerarText.trim()}
                                    className="text-[10px] bg-leadme-pink text-white px-2.5 py-1 rounded hover:bg-leadme-pink-dark disabled:opacity-50"
                                  >
                                    Spara
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {c.text && <p className="text-gray-700">{c.text}</p>}
                                {(c.images || []).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {c.images.map((url, bi) => (
                                      <div key={bi} className="relative">
                                        <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                          <img src={url} alt="" className="h-12 w-12 object-cover rounded border border-gray-300 hover:opacity-80 transition-opacity" />
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Bilderna rensas en tid efter projektavslut, se
                                    backend/scripts/rensaGranskningsbilder.js */}
                                {!(c.images || []).length && c.imagesPurgedAt && (
                                  <p className="text-[10px] text-gray-400 italic mt-1">
                                    {c.purgedImageCount === 1 ? '1 bild' : (c.purgedImageCount || '') + ' bilder'} rensade sedan projektet avslutades
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}

                      {/* Svara-knapp (för IA) */}
                      {canReply && !isReplying && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setReplyingTo(h.id); }}
                          className="text-[10px] text-leadme-pink hover:text-leadme-pink-dark font-medium flex items-center gap-0.5"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          Svara
                        </button>
                      )}

                      {/* Svarsfält */}
                      {canReply && isReplying && (
                        <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                          <textarea
                            autoFocus
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleReply(h.id);
                              }
                              if (e.key === 'Escape') {
                                setReplyingTo(null);
                                setReplyText('');
                              }
                            }}
                            onPaste={(e) => hanteraBildPaste(e, replyBilder.length, (urler) =>
                              setReplyBilder(prev => [...prev, ...urler].slice(0, MAX_BILDER)))}
                            placeholder="Skriv ditt svar, eller klistra in en skärmbild..."
                            rows={2}
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink resize-none"
                          />
                          {(replyBilder || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {replyBilder.map((url, bi) => (
                                <div key={bi} className="relative">
                                  <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <img src={url} alt="" className="h-12 w-12 object-cover rounded border border-gray-300 hover:opacity-80 transition-opacity" />
                                  </a>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setReplyBilder(prev => prev.filter(u => u !== url)); }}
                                    className="absolute -top-1 -right-1 bg-white border border-gray-300 rounded-full w-4 h-4 text-[9px] leading-none text-gray-500 hover:text-red-600"
                                    title="Ta bort bilden"
                                  >&times;</button>
                                </div>
                              ))}
                            </div>
                          )}
                          {laddarBild && <p className="text-[10px] text-gray-500 mt-1">Laddar upp bilden…</p>}
                          {bildFel && <p className="text-[10px] text-red-600 mt-1">{bildFel}</p>}
                          <div className="flex justify-end gap-1 mt-1">
                            <button
                              onClick={() => { setReplyingTo(null); setReplyText(''); setReplyBilder([]); setBildFel(null); }}
                              className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-1"
                            >
                              Avbryt
                            </button>
                            <button
                              onClick={() => handleReply(h.id)}
                              disabled={(!replyText.trim() && replyBilder.length === 0) || laddarBild}
                              className="text-[10px] bg-leadme-pink text-white px-2.5 py-1 rounded hover:bg-leadme-pink-dark disabled:opacity-50"
                            >
                              Spara
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ta bort markeringen — bara innan den har en tråd.
                      Villkoret !h.comments?.length är samma som skiljer
                      redigeringsläget från trådvyn ovan: så länge ingen har
                      svarat är markeringen bara en egen placering man ska kunna
                      ångra. Så fort det finns en tråd försvinner knappen, och
                      man tar bort sitt eget inlägg med papperskorgen vid
                      inlägget i stället. En hel tråd ska aldrig gå att radera —
                      det var så kundens frågor följde med IA:ns egna svar. */}
                  {!readOnly
                    && !h.comments?.length
                    && (!h.authorRole || h.authorRole === authorRole) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); raderaMarkering(h); }}
                      className="text-[10px] text-red-400 hover:text-red-600 mt-2 flex items-center gap-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Ta bort
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}
    </div>
  );
};

export default PdfAnnotationViewer;
